/**
 * Support.js — v6.38.0
 * Public /support landing page reachable via the QR code on every
 * Sealed USB Edition Getting Started Card.
 *
 * Zero auth. Zero data collection. Static page — safe to reach from any
 * classroom Chromebook.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, Clock, AlertTriangle, HelpCircle,
         ShieldCheck, FileText, Video, ChevronRight, Usb } from 'lucide-react';

/**
 * VIDEO_WALKTHROUGHS
 * Filenames + metadata match /app/frontend/public/videos/README.md,
 * which is the drop location for the MP4s (per shot-list PDF).
 *
 * `chapters[]`  — jump-to timestamps rendered as clickable chips beneath
 *                 each player. Derived from the shot list PDF scene breakdown.
 *                 Each: { t: <seconds>, label: <short human title> }.
 * Each entry auto-detects its MP4 via a HEAD fetch; if missing we render
 * a "coming soon" placeholder line instead.
 */
const VIDEO_WALKTHROUGHS = [
  {
    file: 'Video_1_Unboxing_to_First_Score.mp4',
    title: 'Unboxing to first score',
    length: '3:00',
    chapters: [
      { t: 0,   label: 'Unbox the kit' },
      { t: 15,  label: "What's in the box" },
      { t: 35,  label: 'Plug in the USB' },
      { t: 55,  label: 'Open the app' },
      { t: 85,  label: 'Enter your serial' },
      { t: 115, label: 'Score your first student' },
      { t: 155, label: 'Where to get help' },
    ],
  },
  {
    file: 'Video_2_Scoring_a_Class_of_20.mp4',
    title: 'How to score a class of 20',
    length: '5:30',
    chapters: [
      { t: 0,   label: 'Set up at your desk' },
      { t: 20,  label: 'Score the first five' },
      { t: 70,  label: 'Add teacher notes' },
      { t: 140, label: 'See auto-save at work' },
      { t: 200, label: 'Score the middle of the class' },
      { t: 270, label: 'Score the last five' },
      { t: 315, label: 'Class complete' },
    ],
  },
  {
    file: 'Video_3_Reading_the_Class_Report.mp4',
    title: 'Reading the class report',
    length: '2:15',
    chapters: [
      { t: 0,   label: 'Open the class report' },
      { t: 10,  label: 'Read the composite score' },
      { t: 40,  label: 'Read the heatmap' },
      { t: 80,  label: 'Group your students' },
      { t: 115, label: 'Jump to strategy pages' },
    ],
  },
  {
    file: 'Video_4_Export_and_Back_Up_Your_Scores.mp4',
    title: 'Exporting and backing up scores',
    length: '1:45',
    chapters: [
      { t: 0,  label: 'Why back up weekly' },
      { t: 15, label: 'Export the JSON' },
      { t: 40, label: 'Save it to the cloud' },
      { t: 70, label: 'Restore from a backup' },
      { t: 95, label: 'Set a weekly reminder' },
    ],
  },
];

const fmtTime = (s) => {
  const m  = Math.floor(s / 60);
  const ss = String(Math.floor(s % 60)).padStart(2, '0');
  return `${m}:${ss}`;
};

const VideoWalkthrough = ({ file, title, length, chapters = [], index }) => {
  const [status, setStatus] = useState('checking'); // 'checking' | 'present' | 'missing'
  const [activeChapter, setActiveChapter] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`/videos/${file}`, { method: 'HEAD' })
      .then((r) => {
        // Preview infra rewrites unknown paths to the SPA (200 + text/html),
        // so a 200 alone isn't proof — we need a real video content-type.
        const ct = (r.headers.get('content-type') || '').toLowerCase();
        const isVideo = r.ok && ct.startsWith('video/');
        if (!cancelled) setStatus(isVideo ? 'present' : 'missing');
      })
      .catch(() => { if (!cancelled) setStatus('missing'); });
    return () => { cancelled = true; };
  }, [file]);

  const jumpTo = (t, i) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = t;
    setActiveChapter(i);
    v.play().catch(() => { /* autoplay blocked; user can press play */ });
  };

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !chapters.length) return;
    // Find the last chapter whose start time <= currentTime.
    let idx = 0;
    for (let i = 0; i < chapters.length; i += 1) {
      if (chapters[i].t <= v.currentTime) idx = i;
      else break;
    }
    if (idx !== activeChapter) setActiveChapter(idx);
  };

  if (status === 'present') {
    const poster = `/videos/${file.replace(/\.mp4$/i, '.jpg')}`;
    return (
      <div className="space-y-2" data-testid={`support-video-${index}`}>
        <video
          ref={videoRef}
          controls
          preload="metadata"
          poster={poster}
          onTimeUpdate={onTimeUpdate}
          className="w-full rounded border border-slate-300 bg-black"
          data-testid={`support-video-player-${index}`}
        >
          <source src={`/videos/${file}`} type="video/mp4" />
          Your browser does not support HTML5 video.
        </video>
        <p className="text-xs text-slate-600">
          <strong>{title}</strong> · {length}
        </p>
        {chapters.length > 0 && (
          <div
            className="flex flex-wrap gap-1.5 pt-1"
            data-testid={`support-video-chapters-${index}`}
          >
            {chapters.map((c, i) => {
              const isActive = i === activeChapter;
              return (
                <button
                  key={`${c.t}-${c.label}`}
                  type="button"
                  onClick={() => jumpTo(c.t, i)}
                  className={
                    'text-[11px] px-2 py-1 rounded-full border transition-colors ' +
                    (isActive
                      ? 'bg-[#0F1B2D] text-white border-[#0F1B2D]'
                      : 'bg-white text-[#0F1B2D] border-slate-300 hover:bg-slate-100')
                  }
                  data-testid={`support-video-chapter-${index}-${i}`}
                  title={`Jump to ${fmtTime(c.t)}`}
                >
                  <span className="font-mono opacity-70 mr-1">{fmtTime(c.t)}</span>
                  {c.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // 'checking' and 'missing' both show the placeholder — avoids UI flicker.
  // We also list the chapter labels so teachers know what's coming.
  return (
    <div
      className="text-sm py-1"
      data-testid={`support-video-placeholder-${index}`}
    >
      <div className="text-slate-400">
        ▸ {title} ({length}) — <em>coming soon</em>
      </div>
      {chapters.length > 0 && (
        <div className="mt-1 ml-4 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-slate-400 leading-relaxed">
          {chapters.map((c) => (
            <span key={`${c.t}-${c.label}`} className="whitespace-nowrap">
              <span className="font-mono opacity-70">{fmtTime(c.t)}</span> {c.label}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Card = ({ tint, icon: Icon, title, children, testId }) => (
  <div
    className={`rounded-lg border-t-4 ${tint} bg-white shadow-sm p-5`}
    data-testid={testId}
  >
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} className="text-[#0F1B2D]" />
      <h2 className="text-[15px] font-bold text-[#0F1B2D] uppercase tracking-wide">
        {title}
      </h2>
    </div>
    <div className="text-sm text-slate-700 space-y-2">{children}</div>
  </div>
);

const Support = () => {
  return (
    <div className="min-h-screen bg-[#F5F1E8]" data-testid="support-page">
      {/* Hero */}
      <div className="bg-[#0F1B2D] text-white">
        <div className="max-w-5xl mx-auto px-6 py-10 flex items-center gap-6">
          <img
            src="/mosaic-mascot.png"
            alt="MOSAIC mascot"
            className="w-20 h-20 object-contain"
          />
          <div className="flex-1">
            <p className="text-[#F4B740] text-xs font-bold uppercase tracking-widest mb-1">
              Sealed USB Edition · Support Center
            </p>
            <h1 className="text-3xl sm:text-4xl font-bold">MOSAIC Support</h1>
            <p className="text-[#C8CFD9] text-sm mt-2">
              You scanned the QR from your Getting Started Card. Everything a
              teacher, coordinator, or IT admin needs to run MOSAIC in the
              classroom — video walkthroughs, printable reference PDFs, and
              the fastest way to reach a human.
            </p>
          </div>
        </div>
        <div className="bg-[#F4B740] h-1" />
      </div>

      <div className="max-w-5xl mx-auto px-6 py-10 space-y-6">
        {/* URGENT: lost USB */}
        <div
          className="rounded-lg border-l-4 border-[#C2313B] bg-rose-50 p-5 flex items-start gap-4"
          data-testid="support-lost-usb-banner"
        >
          <AlertTriangle className="text-[#C2313B] flex-shrink-0 mt-1" size={28} />
          <div className="flex-1">
            <h2 className="text-lg font-bold text-[#C2313B] mb-1">
              Lost or Stolen USB? Call HQ First — Time Matters
            </h2>
            <p className="text-sm text-slate-700 mb-3">
              We revoke the serial within 15 minutes and ship a replacement
              next business day. Your student data lives on the device
              (browser), not on the USB — so nothing is at risk in transit.
            </p>
            <div className="flex flex-wrap gap-3">
              <a
                href="tel:+19733033414"
                className="inline-flex items-center gap-2 bg-[#C2313B] hover:bg-red-800 text-white font-bold px-4 py-2 rounded"
                data-testid="support-call-hq"
              >
                <Phone size={16} /> Call 973-303-3414
              </a>
              <a
                href="mailto:orders@mosaicassessmentco.com?subject=Lost%20USB%20-%20Serial%20Revocation"
                className="inline-flex items-center gap-2 bg-white border border-[#C2313B] text-[#C2313B] hover:bg-rose-100 font-bold px-4 py-2 rounded"
                data-testid="support-email-hq"
              >
                <Mail size={16} /> Email HQ
              </a>
            </div>
          </div>
        </div>

        {/* 2-col grid */}
        <div className="grid md:grid-cols-2 gap-5">
          <Card
            tint="border-[#F4B740]"
            icon={Usb}
            title="First-Time Setup"
            testId="support-setup"
          >
            <ol className="list-decimal ml-5 space-y-1">
              <li>Plug the USB into any port (Windows / Mac / Chromebook)</li>
              <li>Open the drive and double-click <code>Start MOSAIC.html</code></li>
              <li>Enter the 11-character serial printed on the USB cap</li>
              <li>Bookmark the page — you'll never enter the serial again</li>
            </ol>
            <p className="text-xs text-slate-500 mt-3">
              App works completely offline. English / Spanish toggle in the
              top-right of the scoring screen.
            </p>
          </Card>

          <Card
            tint="border-[#34D399]"
            icon={ShieldCheck}
            title="Safe Eject (Do This Every Time)"
            testId="support-safe-eject"
          >
            <p><strong>Windows</strong> — right-click the USB icon → Eject → wait for "Safe to remove"</p>
            <p><strong>Mac</strong> — press <kbd className="border rounded px-1 text-xs">⌘E</kbd>, or drag the drive to the Trash</p>
            <p><strong>Chromebook</strong> — Files app → click the eject icon next to the drive name</p>
            <p className="text-xs text-[#C2313B] font-bold mt-2">
              Never yank the USB while the scoring app is running — you can
              lose the last few scores if they were mid-save.
            </p>
          </Card>

          <Card
            tint="border-[#60A5FA]"
            icon={HelpCircle}
            title="What to Do If..."
            testId="support-what-if"
          >
            <ul className="space-y-2">
              <li>
                <strong>USB isn't detected</strong> — try another port, check the
                cap light, on Windows respond "Trust this device" if prompted.
              </li>
              <li>
                <strong>Scoring app won't open</strong> — use Chrome or Edge;
                right-click <code>index.html</code> → Open with browser.
              </li>
              <li>
                <strong>Serial rejected</strong> — verify all 11 characters
                (O vs 0 · I vs 1). Still bad? Call HQ to re-mint.
              </li>
              <li>
                <strong>Booklet won't print</strong> — confirm US-Letter paper,
                try "Fit to page", open the PDF in Adobe Reader (not the browser).
              </li>
              <li>
                <strong>Scores disappeared</strong> — Scoring app → Review &amp;
                Export → Import prior JSON if you have a backup.
              </li>
              <li>
                <strong>Need more booklets</strong> — sign in to the{' '}
                <Link
                  to="/teacher-portal"
                  className="text-[#0F1B2D] font-bold underline"
                  data-testid="support-teacher-portal-link"
                >
                  Teacher Portal
                </Link>{' '}
                with your district code.
              </li>
            </ul>
          </Card>

          <Card
            tint="border-[#C2313B]"
            icon={FileText}
            title="Printable References"
            testId="support-downloads"
          >
            <p>Download and reprint any of the reference materials on demand:</p>
            <ul className="space-y-1 mt-2">
              <li>
                <a
                  href="/blm_downloads/MOSAIC_Scoring_and_Reports_Handbook.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#0F1B2D] font-bold hover:underline inline-flex items-center gap-1"
                  data-testid="support-dl-handbook"
                >
                  <ChevronRight size={14} /> Scoring &amp; Reports Handbook (6-page teacher companion)
                </a>
              </li>
              <li>
                <a
                  href="/blm_downloads/MOSAIC_USB_Getting_Started_Card.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#0F1B2D] font-bold hover:underline inline-flex items-center gap-1"
                  data-testid="support-dl-getting-started"
                >
                  <ChevronRight size={14} /> Getting Started Card (this page in PDF)
                </a>
              </li>
              <li>
                <a
                  href="/sales/MOSAIC_USB_Standalone_Hero_Fact_Sheet.pdf"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#0F1B2D] font-bold hover:underline inline-flex items-center gap-1"
                  data-testid="support-dl-fact-sheet"
                >
                  <ChevronRight size={14} /> USB Fact Sheet — pricing and FAQ
                </a>
              </li>
              <li>
                <a
                  href="/usb/EULA.txt"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#0F1B2D] font-bold hover:underline inline-flex items-center gap-1"
                  data-testid="support-dl-eula"
                >
                  <ChevronRight size={14} /> End-User License Agreement (EULA)
                </a>
              </li>
            </ul>
          </Card>

          <Card
            tint="border-[#94A3B8]"
            icon={Video}
            title="Video Walkthroughs"
            testId="support-videos"
          >
            <p className="text-slate-600 text-xs mb-3">
              Each slot flips from a placeholder to a live, chaptered player
              the moment its MP4 lands in <code>/videos/</code>. Tap any
              chapter chip to jump straight to that step — no scrubbing.
            </p>
            <div className="space-y-4">
              {VIDEO_WALKTHROUGHS.map((v, i) => (
                <VideoWalkthrough key={v.file} index={i + 1} {...v} />
              ))}
            </div>
          </Card>

          <Card
            tint="border-[#0F1B2D]"
            icon={Clock}
            title="Talk to a Human"
            testId="support-contact"
          >
            <p className="text-2xl font-bold text-[#0F1B2D]">
              <a href="tel:+19733033414" className="hover:underline">
                973-303-3414
              </a>
            </p>
            <p className="text-slate-600">
              Mon-Fri 8AM-6PM ET · text OK · response under 4 business hours
            </p>
            <p className="mt-2">
              <a
                href="mailto:orders@mosaicassessmentco.com"
                className="text-[#0F1B2D] font-bold hover:underline"
              >
                orders@mosaicassessmentco.com
              </a>
            </p>
            <p className="text-xs text-slate-500 mt-3">
              For lost / stolen USBs we respond same-day. For content or
              scoring questions, one of our reps in your NYC borough will get
              back to you within four business hours.
            </p>
          </Card>
        </div>

        {/* Footer strip */}
        <div className="mt-8 pt-6 border-t border-slate-300 text-center text-xs text-slate-500">
          <p className="font-bold tracking-wider text-[#0F1B2D]">
            IDEAS · PEOPLE · PROGRESS · TOGETHER
          </p>
          <p className="mt-2">
            MOSAIC Assessment Co. · Lerno Co., Belleville, NJ 07109 ·{' '}
            NYC DOE Vendor Code LER599869
          </p>
        </div>
      </div>
    </div>
  );
};

export default Support;
